import React from 'react';
import Modal from 'react-bootstrap/Modal';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { useDispatch } from 'react-redux';
import { strings } from '../../resources/strings';
import ServicesContext from '../../contexts/ServicesContext';
import { useAuth } from '../../contexts/AuthContext';
import { StatePrescription } from '../../models/StatePrescription';
import { OrderForListing } from '../../models/OrderForListing';
import { CurrentState } from '../../models/CurrentState';
import { MainActions } from '../../actions/MainActions';
import { SaveStatePrescriptionLogInput } from '../../services/StatesPrescriptions/SaveStatePrescriptionLog/SaveStatePrescriptionLogInput';
import { Field, FieldType } from '../../common-components/Field/Field';
import { Button } from '../../common-components/Button/Button';
import { SaveOrderLogInput } from '../../services/OrderLogs/SaveOrderLog/SaveOrderLogInput';
import { OrderLogType } from '../../enums/OrderLogType';

interface IModalUpdatePrescriptionStateForSelectedOrdersProps {
  selectedOrders?: OrderForListing[];
  userId?: number;
  close: () => void;
  finish: () => void;
}

const ModalUpdatePrescriptionStateForSelectedOrders: React.FC<IModalUpdatePrescriptionStateForSelectedOrdersProps> = ({
  selectedOrders,
  userId,
  close,
  finish
}) => {

  const dispatch = useDispatch();
  const mainActions = React.useMemo(() => new MainActions(dispatch), [dispatch]);

  const { getChannel } = useAuth();
  const [states, setStates] = React.useState<StatePrescription[]>([]);
  const [stateId, setStateId] = React.useState<number>(0);
  const [stateDescription, setStateDescription] = React.useState<string>('');
  const [updating, setUpdating] = React.useState<boolean>(false);
  const [cancelUpdating, setCancelUpdating] = React.useState<boolean>(false);
  const [updatingPercent, setUpdatingPercent] = React.useState<number>(0);

  const updatingError = React.useRef(0);
  const updatingErrorServerNotResponding = React.useRef(0);
  const updatingIndex = React.useRef(0);
  const cancel = React.useRef(false);
  const eligibleOrders = React.useRef<CurrentState[] | null>(null);
  const orderLogParentForUpdatePrescriptionStateInBatch = React.useRef<number | null | undefined>(null);

  const {
    statesPrescriptionsService,
    alertService,
    toastService,
    saveStatePrescriptionLogService,
    orderForListingService,
    saveOrderLogService
  } = React.useContext(ServicesContext);

  React.useEffect(() => {
    setStates(statesPrescriptionsService.get());
  }, [statesPrescriptionsService]);

  const memoBodyRender = React.useMemo(() => {
    if (updating) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ marginBottom: '10px' }}>{stateDescription ? `Aguarde, alterando para ${stateDescription}...` : 'Aguarde, alterando...'}</span>
          <ProgressBar now={updatingPercent} label={`${updatingPercent}%`} variant='success' />
        </div>
      );
    }

    return (
      <>
        <Field
          type={FieldType.Select}
          label={strings.InformTheStateOfDegree}
          marginRight='5px'
          value={stateId}
          selectProps={{
            options: [{ id: 0, name: strings.None }, ...states],
            displayField: 'description',
            keyField: 'id'
          }}
          change={(value) => setNewStateId(value)}
        />
      </>
    );
  }, [updating, states, stateId, updatingPercent]);

  const memoFooterRender = React.useMemo(() => {
    if (updating) {
      return (
        <Button text={strings.Cancel} disabled={cancelUpdating} click={() => cancelNewStateOrder()} />
      );
    }

    return (
      <>
        <Button text={strings.Cancel} click={() => close()} marginRight={'2px'} />
        <Button text={strings.Confirm} click={() => confirmNewStateOrder()} marginLeft={'2px'} />
      </>
    );
  }, [updating, stateId, cancelUpdating]);  //don't remove stateId

  return (
    <Modal
      show={true}
      onHide={() => close()}
      backdrop="static"
      keyboard={false}

    >
      <Modal.Header>
        <Modal.Title>
          {strings.ChangeStateOfDegree}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: '78vh', overflowY: 'auto', overflowX: 'hidden' }}>
        {memoBodyRender}
      </Modal.Body>
      <Modal.Footer>
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {memoFooterRender}
        </div>
      </Modal.Footer>
    </Modal>
  );

  function setNewStateId(stateId: number) {
    setStateId(+stateId);
    const newStateDescription = states?.find(xs => xs.id === +stateId)?.description;
    setStateDescription(newStateDescription || '');
  }

  async function confirmNewStateOrder() {
    if (!stateId) {
      toastService.warning(strings.InformTheStateOfOrder);
      return;
    }

    const eligibleList = selectedOrders ? await orderForListingService.getEligibleOrdersToUpdatePrescriptionState(selectedOrders, stateId) : [];
    if (!eligibleList || eligibleList.length === 0) {
      close();
      return;
    }

    const secondsByEligibleItemRequest = 4;
    const necessaryChannelSeconds = eligibleList.length * secondsByEligibleItemRequest;
    const necessaryChannelMinutes = Math.ceil(necessaryChannelSeconds / 60);
    const channel = await getChannel(necessaryChannelMinutes);
    console.log('channel: ', channel);
    if (channel.remainingMinutes <= 0) {
      if (channel.message?.type === 'question') {
        const response = await alertService.confirm(channel.message.content);
        if (!response.isConfirmed) return;
      } else if (channel.message?.content) {
        alertService.warning(channel.message?.content);
        return;
      }
    }
    if (channel.message?.content) {
      alertService.warning(channel.message?.content);
    }

    orderLogParentForUpdatePrescriptionStateInBatch.current = await getOrderLogParentForUpdatePrescriptionStateInBatch(eligibleList);

    eligibleOrders.current = [...eligibleList];
    console.log('eligible orders to update: ', eligibleOrders.current);
    setUpdating(true);
    setCancelUpdating(false);
    updatingError.current = 0;
    updatingIndex.current = 0;
    cancel.current = false;
    updateNextOrder(0);
    mainActions.setNotWait(true);
  }

  async function getOrderLogParentForUpdatePrescriptionStateInBatch(eligibleList: CurrentState[]) {
    const saveOrderLogInput = new SaveOrderLogInput();
    saveOrderLogInput.userId = userId;
    saveOrderLogInput.orderLogType = OrderLogType.UpdatePrescriptionStateInBatch;
    saveOrderLogInput.details = `Pedidos alterados para Grau ${stateDescription}: ` + eligibleList.map(item => item.number.toString()).join(', ');
    const saveOrderLogOutput = await saveOrderLogService.handle(saveOrderLogInput);
    return saveOrderLogOutput?.value?.result && saveOrderLogOutput?.value?.orderLogId
      ? saveOrderLogOutput.value.orderLogId
      : null;
  }

  function cancelNewStateOrder() {
    setCancelUpdating(true);
    cancel.current = true;
    mainActions.setNotWait(false);
  }

  async function updateNextOrder(orderPosition: number) {
    if (cancelUpdating || cancel.current) {
      setUpdating(false);
      return;
    }

    const orderForListing = selectedOrders && orderPosition < selectedOrders.length ? selectedOrders[orderPosition] : null;

    if (!orderForListing) {
      setUpdatingPercent(100);
      setTimeout(() => finishUpdating(), 1000);
      return;
    }

    const orderEligibleForChange = orderForListing && eligibleOrders.current
      ? eligibleOrders.current.filter(xeo => xeo.orderId === orderForListing.order.id).length > 0
      : false;

    let percent = selectedOrders && selectedOrders.length > 0 ? (orderPosition / selectedOrders.length) * 100 : 0;
    percent = +percent.toFixed(0);

    var input = new SaveStatePrescriptionLogInput();
    input.orderId = orderForListing.order.id;
    input.newStatePrescriptionId = stateId;
    input.userId = userId || 0;

    let updateError = false;
    let serverResponse = false;
    if (orderEligibleForChange) {
      console.log(`order eligible to update: ${orderForListing.order.number}`);
      try {
        const result = await saveStatePrescriptionLogService.handle(input);
        if (!result?.value?.result) {
          updateError = true;
        }
      } catch (error: any) {
        serverResponse = error?.response;
        updateError = true;
      }

      if (updateError) {
        await saveOrderLogForOrder(orderForListing.order.id, 'Realizada sem sucesso.', orderLogParentForUpdatePrescriptionStateInBatch.current);
      } else {
        await saveOrderLogForOrder(orderForListing.order.id, 'Realizada com sucesso.', orderLogParentForUpdatePrescriptionStateInBatch.current);
      }
    } else {
      console.log(`order ineligible to update: ${orderForListing.order.number}`);
    }

    if (updateError) {
      updatingError.current = updatingError.current + 1;

      if (!serverResponse) updatingErrorServerNotResponding.current = updatingErrorServerNotResponding.current + 1;
    }

    setUpdatingPercent(percent);

    if (orderEligibleForChange) {
      updatingIndex.current = updatingIndex.current + 1;
    }

    updateNextOrder(orderPosition + 1);
  }

  async function saveOrderLogForOrder(orderId: number, details: string, parentId?: number | null) {
    const saveOrderLogInput = new SaveOrderLogInput();
    saveOrderLogInput.userId = userId;
    saveOrderLogInput.orderId = orderId;
    saveOrderLogInput.orderLogType = OrderLogType.UpdatePrescriptionStateInBatch;
    saveOrderLogInput.details = details;
    saveOrderLogInput.parentId = parentId;
    return await saveOrderLogService.handle(saveOrderLogInput);
  }

  async function finishUpdating() {
    mainActions.setNotWait(false);
    if (updatingError.current > 0) {
      const selectedOrdersNumber = selectedOrders?.length || 0;
      const realNumberError = updatingError.current - updatingErrorServerNotResponding.current;
      const updatingSuccess = selectedOrdersNumber - updatingError.current;
      if (updatingSuccess > 0) {
        let message = `De ${selectedOrdersNumber} pedido`;
        if (selectedOrdersNumber > 1) message += 's';
        message += ' selecionado';
        if (selectedOrdersNumber > 1) message += 's';
        message += ` ${updatingSuccess}`;
        if (updatingSuccess > 1) {
          message += ` foram alterados`;
        } else {
          message += ` foi alterado`;
        }
        message += ` com sucesso`;
        if (realNumberError > 0) {
          message += ` e ${realNumberError}`;
          if (realNumberError > 1) {
            message += ` falharam.`;
          } else {
            message += ` falhou.`;
          }
        } else {
          message += ` e pode ser que em algum caso o sistema falhou.`;
        }
        if (updatingErrorServerNotResponding.current > 0) {
          if (updatingErrorServerNotResponding.current > 1) {
            message += ` Para ${updatingErrorServerNotResponding.current} casos o servidor não respondeu.`;
          } else {
            message += ` Para um caso o servidor não respondeu. `;
          }
          message += ' Portanto não tem como saber se realmente falhou.'
        }
        await alertService.info(`${message} ${strings.TryAgainAndInformTechTeamIfProblemPersists}`);
      } else if (updatingErrorServerNotResponding.current > 0) {
        let message = `De ${selectedOrdersNumber} pedido`;
        if (selectedOrdersNumber > 1) message += 's';
        message += ' selecionado';
        if (selectedOrdersNumber > 1) message += 's';
        if (realNumberError > 0) {
          message += ` ${realNumberError}`;
          if (realNumberError > 1) {
            message += ` falharam.`;
          } else {
            message += ` falhou.`;
          }
        } else {
          message += ` pode ser que em algum caso o sistema falhou.`;
        }
        if (updatingErrorServerNotResponding.current > 0) {
          if (updatingErrorServerNotResponding.current > 1) {
            message += ` Para ${updatingErrorServerNotResponding.current} casos o servidor não respondeu.`;
          } else {
            message += ` Para um caso o servidor não respondeu. `;
          }
          message += 'Portanto não tem como saber se realmente falhou.'
        }
        await alertService.info(`${message} ${strings.TryAgainAndInformTechTeamIfProblemPersists}`);
      } else {
        await alertService.info(`${strings.UpdatingFailedForAllOrders} ${strings.TryAgainAndInformTechTeamIfProblemPersists}`);
      }
    } else {
      await alertService.info(strings.UpdatingFinishedWithSuccess);
    }
    setUpdating(false);
    finish();
  }
}

export { ModalUpdatePrescriptionStateForSelectedOrders };